import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import CoinItem from '../../components/common/CoinItem';
import BackButton from '../../components/common/BackButton';
import './UserProfile.css';

function UserProfile() {
  const auth = useAuth();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [balances, setBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transactionsToShow, setTransactionsToShow] = useState(5);
  const [isEditingJobTitle, setIsEditingJobTitle] = useState(false);
  const [editJobTitle, setEditJobTitle] = useState('');
  const [savingJobTitle, setSavingJobTitle] = useState(false);

  // Check if this is the current user's profile
  const isOwnProfile = auth.user?.profile?.sub === userId;

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!auth.isAuthenticated || !auth.user?.id_token) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const idToken = auth.user.id_token;

        // Fetch user profile, balances, and transactions in parallel
        const [userData, balancesData, transactionsData] = await Promise.all([
          apiService.getUserProfile(idToken, userId),
          apiService.getUserBalancesById(idToken, userId, null), // null = no filter, get all balances including negative
          apiService.getUserTransactionsById(idToken, userId, { type: 'received', limit: transactionsToShow })
        ]);

        setUser(userData);
        setBalances(balancesData.balances || []);
        setTransactions(transactionsData.transactions || []);



      } catch (err) {
        console.error('Error fetching user profile:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserProfile();
    }
  }, [userId, auth.isAuthenticated, auth.user, transactionsToShow]);

  const calculateTotalCoins = () => {
    let individualReceived = 0;
    let organizationalReceived = 0;
    let totalSent = 0;

    balances.forEach(balance => {
      const received = (balance.coins_received_from_individuals || 0) +
                      (balance.coins_received_from_organizations || 0);

      // Sum received coins by type
      if (balance.coin_type === 'individual') {
        individualReceived += received;
      } else if (balance.coin_type === 'organizational') {
        organizationalReceived += received;
      }

      // Sum all sent coins
      totalSent += (balance.coins_sent || 0);
    });

    return {
      individual: individualReceived,
      organizational: organizationalReceived,
      total: individualReceived + organizationalReceived,
      sent: totalSent
    };
  };

  // Format timestamp to relative time
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return diffMins <= 1 ? 'Just now' : `${diffMins} minutes ago`;
    } else if (diffHours < 24) {
      return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    } else if (diffDays < 7) {
      return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleEditJobTitle = () => {
    setEditJobTitle(user.job_title || '');
    setIsEditingJobTitle(true);
  };

  const handleSaveJobTitle = async () => {
    setSavingJobTitle(true);
    try {
      const idToken = auth.user.id_token;
      const updatedUser = await apiService.updateUser(idToken, {
        display_name: user.display_name,
        job_title: editJobTitle.trim() || null
      });
      setUser(updatedUser);
      setIsEditingJobTitle(false);
      window.dispatchEvent(new Event('profileUpdated'));
    } catch (err) {
      console.error('Error updating job title:', err);
    } finally {
      setSavingJobTitle(false);
    }
  };

  const handleCancelEditJobTitle = () => {
    setIsEditingJobTitle(false);
  };

  if (loading) {
    return (
      <div className="user-profile-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-profile-container">
        <div className="error-container">
          <h2>Error Loading Profile</h2>
          <p>{error}</p>
          <BackButton label="Go Back" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-profile-container">
        <div className="error-container">
          <h2>User Not Found</h2>
          <p>The user you're looking for doesn't exist.</p>
          <BackButton label="Go Back" />
        </div>
      </div>
    );
  }

  const coinTotals = calculateTotalCoins();

  // Calculate net coins (received - sent)
  // const netCoins = coinTotals.total - coinTotals.sent;

  // Debug: Log calculation details
  return (
    <div className="user-profile-container">
      {/* Profile Header + My Coins */}
      <div className="profile-top-row">
        <section className={`profile-header ${isOwnProfile ? 'own-profile' : 'other-profile'}`}>
          <div className="profile-avatar-large">
            {user.display_name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="profile-info">
            <h1 className="profile-display-name">{user.display_name}</h1>
            {isEditingJobTitle ? (
              <div className="job-title-edit">
                <input
                  type="text"
                  value={editJobTitle}
                  onChange={(e) => setEditJobTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveJobTitle();
                    if (e.key === 'Escape') handleCancelEditJobTitle();
                  }}
                  className="job-title-input"
                  placeholder="Enter job title..."
                  autoFocus
                  disabled={savingJobTitle}
                />
                <button onClick={handleSaveJobTitle} className="job-title-save-btn" disabled={savingJobTitle}>
                  {savingJobTitle ? '...' : '✓'}
                </button>
                <button onClick={handleCancelEditJobTitle} className="job-title-cancel-btn" disabled={savingJobTitle}>
                  ✕
                </button>
              </div>
            ) : (
              <div
                className={`job-title-display ${isOwnProfile ? 'editable' : ''}`}
                onClick={isOwnProfile ? handleEditJobTitle : undefined}
                role={isOwnProfile ? 'button' : undefined}
                tabIndex={isOwnProfile ? 0 : undefined}
                onKeyDown={isOwnProfile ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleEditJobTitle();
                  }
                } : undefined}
                title={isOwnProfile ? 'Edit job title' : undefined}
              >
                {user.job_title ? (
                  <p className="profile-job-title">{user.job_title}</p>
                ) : isOwnProfile ? (
                  <p className="profile-job-title placeholder">Add job title</p>
                ) : null}
                {isOwnProfile && (
                  <svg className="job-title-pencil" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                    <path d="m15 5 4 4"/>
                  </svg>
                )}
              </div>
            )}
            <p className="profile-email">{user.email}</p>
            {isOwnProfile && <span className="own-profile-badge">Your Profile</span>}
            <div className="profile-type-badge">
              <span className="type-icon">
                {user.user_type === 'individual' ? '👤' : user.user_type === 'developer' ? '💻' : '🏢'}
              </span>
              <span className="type-text">
                {user.user_type === 'individual' ? 'Individual' : user.user_type === 'developer' ? 'Developer' : 'Organization'}
              </span>
            </div>
          </div>
        </section>

        <div
          className="profile-coin-card individual clickable-card"
          onClick={() => navigate(`/profile/${userId}/coins`)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`/profile/${userId}/coins`);
            }
          }}
          title="View all your coins"
        >
          <div className="coin-card-header">
            <span className="coin-icon">🪙</span>
            <h3>{isOwnProfile ? 'My Coins' : `${user.display_name?.split(' ')[0]}'s Coins`}</h3>
          </div>
          <p className="coin-description">
            {coinTotals.total} coin{coinTotals.total !== 1 ? 's' : ''} received
          </p>
        </div>
      </div>

      {/* Organizational Coins card - temporarily disabled */}
      {/* <section className="profile-coins-summary">
        <div
          className="profile-coin-card organizational clickable-card"
          onClick={() => navigate(`/profile/${userId}/coins?type=organizational`)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`/profile/${userId}/coins?type=organizational`);
            }
          }}
          title="View all organizational coins"
        >
          <div className="coin-card-header">
            <span className="coin-icon">🏆</span>
            <h3>Organizational Coins</h3>
          </div>
          <div className="coin-count">{coinTotals.organizational}</div>
          <p className="coin-description">Coins earned from organizations</p>
        </div>
      </section> */}

      {/* Recent Recognition */}
      <section className="profile-transactions-section">
        <div className="section-header">
          <h2>Recent Recognition</h2>
          <div className="coins-controls">
            <label htmlFor="coins-limit">Show:</label>
            <select
              id="coins-limit"
              value={transactionsToShow}
              onChange={(e) => setTransactionsToShow(Number(e.target.value))}
              className="coins-select"
            >
              <option value={3}>Last 3</option>
              <option value={5}>Last 5</option>
              <option value={7}>Last 7</option>
              <option value={10}>Last 10</option>
            </select>
            <button
              onClick={() => navigate(`/profile/${userId}/coins`)}
              className="view-all-coins-button"
            >
              View All Coins
            </button>
          </div>
        </div>

        {transactions.filter(tx => !tx.pending).length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">🎉</span>
            <p>No recognition received yet</p>
          </div>
        ) : (
          <div className="coins-list">
            {transactions.filter(tx => !tx.pending).map((tx) => (
              <CoinItem
                key={tx.tx_id}
                id={tx.tx_id}
                coinType={tx.coin_type}
                senderName={tx.sender_name || 'Unknown'}
                senderId={tx.sender_id}
                message={tx.description || 'No description'}
                timestamp={formatTimestamp(tx.created_at)}
                senderEmail={tx.sender_email}
                senderJobTitle={tx.sender_job_title}
                direction="received"
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default UserProfile;
